import { Component, effect, inject, input, OnDestroy, output, Signal, signal } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FieldDescriptor, UserInfoService } from '../../../services/user/user-info.service';
import { UserProfileService } from '../../../services/user/user-profile.service';
import { UserService } from '../../../services/user/user.service';
import { EnvironmentService } from '../../../services/environment.service';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { ArrayService } from '../../../services/array.service';
import { BtnComponent } from '../btn/btn.component';
import { PhoneFormComponent } from '../phone-form/phone-form.component';
import { DobMaskDirective } from '../../directives/dob-mask.directive';
import { NgxMaskDirective } from 'ngx-mask';
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { UserFieldDescriptor, UserFieldType } from '../../../helpers/user-fields.data';
import { TranslatePipe } from '../../translation/translate.pipe';
import { FormInputComponent } from '../../../modules/form-controls/form-input/form-input.component';
import { SelectComponent } from '../../../modules/select/select.component';
import { SelectHeaderComponent } from '../../../modules/select/select-header/select-header.component';
import { SelectDropdownComponent } from '../../../modules/select/select-dropdown/select-dropdown.component';
import { SelectOptionComponent } from '../../../modules/select/select-option/select-option.component';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { LocalstorageService } from 'ngx-unificator/services';
import { decodeFromBase64, decodeFromHex, encodeToBase64, encodeToHex } from '../../../helpers/utils';
import { additionalInfoStorageName } from '../../../helpers/user/register-form-controller';

type UField = UserFieldDescriptor & FieldDescriptor;

@Component({
  selector: 'app-auth-fields-missed',
  templateUrl: './auth-fields-missed.component.html',
  styleUrls: ['./auth-fields-missed.component.scss'],
  imports: [
    ReactiveFormsModule,
    NgTemplateOutlet,
    NgxMaskDirective,
    NgClass,
    DobMaskDirective,
    PhoneFormComponent,
    FormInputComponent,
    SelectComponent,
    SelectHeaderComponent,
    SelectDropdownComponent,
    SelectOptionComponent,
    BtnComponent,
    AsyncPipe,
    TranslatePipe,
  ],
  standalone: true,
})
export class AuthFieldsMissedComponent implements OnDestroy {
  private _fb = inject(FormBuilder);
  private _array = inject(ArrayService);
  private _localStorage = inject(LocalstorageService);
  userProfile = inject(UserProfileService);
  user = inject(UserService);
  userInfo = inject(UserInfoService);
  env = inject(EnvironmentService);

  readonly fieldsList$ = input<Observable<UField[]>>(undefined);
  readonly hideTitle = input<boolean>(false);
  readonly buttonName = input<string>('btn.save');
  readonly onSubmit$ = output<FormGroup>();

  public form: any = this._fb.group({});
  public readonly FieldType = UserFieldType;

  public fieldList = signal<UField[]>([]);

  private fieldsFromInput: Signal<UField[]> = toSignal(
    toObservable(this.fieldsList$).pipe(
      switchMap((obs) => obs ?? of([]))
    ),
    { initialValue: [] }
  );

  constructor() {
    effect(() => {
      const incoming = this.fieldsFromInput();
      if (!incoming?.length) return;

      const sorted = this._array.mapOrder(
        incoming.filter(f => f.field !== 'nickname'),
        [
          'first_name',
          'last_name',
          'gender',
          'date_of_birth',
          'country',
          'state',
          'city',
          'address',
          'postal_code',
          'mobile_phone',
        ],
        'field'
      ) as UField[];

      this.fieldList.set(sorted);
      this.form = this._buildForm(sorted);
      this._restoreForm();

      const envCountry = this.env.dataEnv()?.country?.short?.toUpperCase();
      const countryCtrl = this.form.get('country');
      if (envCountry && countryCtrl) {
        countryCtrl.setValue(envCountry);
        countryCtrl.disable();
      }

      this.userInfo.initAutocomplete(this.form);
    });
  }

  public onSubmit() {
    this.onSubmit$.emit(this.form);
  }

  private _buildForm(fields: UField[]): FormGroup {
    const group = this._fb.group({});
    fields.forEach(field => {
      group.addControl(field.field, this._fb.control(null, field.validators) as FormControl);
    });
    return group;
  }

  private _persistForm(): void {
    try {
      const raw = this.form.getRawValue();
      const json = JSON.stringify(raw);
      const b64 = encodeToBase64(json);
      const hex = encodeToHex(b64);
      this._localStorage.set(additionalInfoStorageName, hex);
    } catch (e) {
      console.log('on saving form', e);
    }
  }

  private _restoreForm(): void {
    try {
      const savedHex = this._localStorage.get(additionalInfoStorageName);
      if (!savedHex) return;

      const b64 = decodeFromHex(savedHex);
      const json = decodeFromBase64(b64);
      const saved = JSON.parse(json) as Record<string, unknown>;

      const patch: Record<string, unknown> = {};
      Object.keys(saved).forEach(key => {
        if (this.form.contains(key)) {
          patch[key] = saved[key];
        }
      });

      if (Object.keys(patch).length) {
        this.form.patchValue(patch, { emitEvent: false });
      }
    } catch (e) {
      console.log(e);
    }
  }

  ngOnDestroy() {
    this._persistForm();
  }
}
